import {
    Component,
    OnInit,
    ViewChild,
    QueryList,
    AfterViewInit,
    ViewChildren,
    ChangeDetectorRef, DestroyRef, inject
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Observable, of, defer, BehaviorSubject, finalize} from 'rxjs';
import {
    filter,
    map,
    take,
    tap,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {KEYS, TREE_ACTIONS, TreeNode, TreeModel} from '@ali-hm/angular-tree-component';
import {
    AddItemToMenuDialogComponent
} from '@emm/shared/dialogs/add-item-to-menu-dialog/add-item-to-menu-dialog.component';
import {DeleteItemDialogComponent} from '@emm/shared/dialogs/delete-item-dialog/delete-item-dialog.component';
import {itemGroupingsFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/item-groupings-filter.config';
import {ItemsFormComponent} from '../items-form/items-form.component';
import {GenericItemsTreeComponent} from '../generic-items-tree/generic-items-tree.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Item} from '@emm/core/models/item.model';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {ItemGroupingsFacadeService} from '@emm/management/services/item-groupings-facade.service';
import {ItemGroupingService} from '@emm/core/services/item-grouping.service';
import {ItemsFacadeService} from '@emm/management/services/items-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {SearchFilter} from '@emm/core/models/search-filter';
import {TreeUtil} from '@emm/core/util/angular-tree-component.util';
import {UserService} from '@emm/core/services/user.service';
import {RemoveItemDialogComponent} from '@emm/shared/dialogs/remove-item-dialog/remove-item-dialog.component';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {API_ERRORS} from '@emm/core/util/api-constants';

@Component({
    selector: 'emm-item-groupings',
    styleUrls: ['./item-groupings.component.scss'],
    templateUrl: './item-groupings.component.html',
    standalone: false
})
export class ItemGroupingsComponent implements OnInit, AfterViewInit {

    @ViewChildren(ItemsFormComponent) public components: QueryList<ItemsFormComponent>;
    @ViewChild(ItemsFormComponent) itemGroupingsForm: ItemsFormComponent;
    @ViewChild(GenericItemsTreeComponent) tree: GenericItemsTreeComponent;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    anyLoading$ = this.itemGroupingsFacade.anyLoading$;
    centerLoading$ = this.itemGroupingsFacade.centerLoading$;
    fields = [fieldMap['name'](), fieldMap['tag']()];
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    isMMSAuthor = this.userService.isMmsAuthorOnly();
    itemDetail$ = this.itemGroupingsFacade.itemDetail$;
    nodes = [];
    nodes$ = new BehaviorSubject<any[]>(undefined);
    nodesCache = [];
    prevItem: Item;
    rightLoading$ = this.itemGroupingsFacade.rightLoading$;

    itemGroupings$ = new BehaviorSubject<any>(undefined);
    itemGroupingsAsNodes$ = this.itemGroupingsFacade.itemGroupingsAsNodes$;
    removeChildToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.REMOVE_CHILD_ITEM_FROM_GROUPING);
    isSearchMode = false;

    options = {
        getChildren: (node: TreeNode) => this.itemGroupingsFacade.getAllChildItems(node.id).toPromise(),
        actionMapping: {
            mouse: {
                click: (tree: TreeModel, node: TreeNode) => {
                    if (node?.isRoot && node?.data?.data) {
                        this.selectItem(node);
                    }
                },
                drop: (tree, node, $event, {from, to}) => {
                    const fromPath = from.data.id;
                    const fromPathParentTag = from.parent.id;
                    const toPath = to.parent.id;

                    this.itemGroupingService
                        .moveItem(toPath, fromPath, fromPathParentTag)
                        .pipe(
                            takeUntilDestroyed(this.destroyRef)
                        )
                        .subscribe(() => {
                            TREE_ACTIONS.MOVE_NODE(tree, node, $event, {from, to});
                        });
                },
            },
            keys: {
                [KEYS.ENTER]: (tree, node) => alert(`This is ${node.data.name}`),
            },
        },
        allowDrag: (node) => {
            return !this.isReadOnly && node.parent && !node.isRoot;
        }
    };

    itemGroupingsContextMenuActions = [
        {
            // Add
            enabled: R.T,
            click: (node) => this.addChildItem(node),
            visible: (node) => this.isSuperAdmin ? node?.data?.data?.grouping : false,
            icon: R.always('add'),
            text: R.always('Add Item')
        },
        {
            // Disable
            enabled: R.T,
            click: (node) => this.toggleEnabled(node),
            visible: (node) => this.isSuperAdmin && node.isRoot ? R.T : R.F,
            icon: (node) => (ItemGroupingsComponent.isEnabled(node) ? 'do_not_disturb_off' : 'do_not_disturb_on'),
            text: (node) => (ItemGroupingsComponent.isEnabled(node) ? 'Disable' : 'Enable')
        },
        {
            // Delete/Remove
            enabled: (node) => (node.parent && node.isRoot ? !node.data.data.locked : true),
            click: (node) => node.isRoot || !this.getToggleValue() ? this.deleteItem(node) : this.removeItem(node),
            visible: this.isSuperAdmin ? R.T : R.F,
            icon: R.always('delete'),
            text: (node) => (node.isRoot ? 'Delete' : 'Remove')
        },
    ] as ContextMenuAction<TreeNode>[];

    static isEnabled(node: TreeNode): boolean {
        return !node?.data?.data?.disabled;
    }

    constructor(
        private authFacade: AuthFacadeService,
        private dialogService: DialogService,
        private itemGroupingsFacade: ItemGroupingsFacadeService,
        private itemGroupingService: ItemGroupingService,
        private itemsFacade: ItemsFacadeService,
        private notificationService: NotificationService,
        private changeDetector: ChangeDetectorRef,
        private dialog: MatDialog,
        private userService: UserService,
        private launchDarklyService: LaunchDarklyService,
    ) {
    }


    ngOnInit(): void {
        this.itemGroupingsFacade.init();
        this.itemGroupings$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.anyLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.getItemGroupings();
        this.itemsFacade.updateItem$
            .pipe(
                filter(RA.isTrue),
                withLatestFrom(this.itemsFacade.itemDetail$),
                map(([_, itemGroup]) => itemGroup),
                filter(RA.isNotNil),
                map((itemGroup) =>
                    this.tree.menuTree.treeModel.getNodeBy(
                        (item: TreeNode) =>
                            RA.isNonEmptyArray(item.data.children) &&
                            item.data.children.some((child) => child.data.tag === itemGroup.tag)
                    )
                ),
                filter(RA.isNotNil),
                switchMap((parentNode) => defer(() => parentNode.loadNodeChildren())),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.authFacade.isReadOnly$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((val) => (this.isReadOnly = val));

        this.itemGroupingsFacade.updateNode$
            .pipe(
                map((updatedTag) => {
                    const someNode = this.tree.menuTree.treeModel.getNodeById(updatedTag);
                    this.getItemGroupings();
                    this.selectItem(someNode);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }


    getItemGroupings() {
        this.itemsFacade.startProgressBar();
        this.itemGroupingsAsNodes$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.itemsFacade.stopProgressBar())
            )
            .subscribe((items) => {
                this.nodes = items.map((item) => JSON.parse(JSON.stringify(item)));
                this.nodes$.next(this.nodes);
                this.nodesCache = this.nodes;
            });
    }


    ngAfterViewInit() {
        this.components.changes
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((comps: QueryList<ItemsFormComponent>) => {
            this.itemGroupingsForm = comps.first;
            this.changeDetector.detectChanges();
        });
    }

    onFilter(searchFilter: SearchFilter): void {
        this.filterNodes(searchFilter.value as string, searchFilter.key);
    }


    filterNodes(value: string, selected: string): void {
        this.isSearchMode = true;
        if (RA.isNonEmptyString(value)) {
            this.nodes = [];
            this.nodesCache.forEach((node) => {
                const match = TreeUtil.filter(value, selected, node);
                if (match) {
                    this.nodes.push(node);
                }
                this.nodes$.next(this.nodes);
            });
        } else {
            this.isSearchMode = false;
            this.itemsFacade.startProgressBar();
            setTimeout(() => {
                this.getItemGroupings();
            }, 1000);
        }
    }


    addChildItem(node: TreeNode): void {
        const dialogRef = this.dialog.open(AddItemToMenuDialogComponent, {
            width: '600px',
            data: {
                itemTypeFilter: null,
                displayAddGlobal: true
            }
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter((child) => !!child),
                switchMap((child) =>
                    this.itemGroupingsFacade.api.addChildItems(node.data.data.tag, child.value, child.addGlobal)
                ),
                switchMap(() => defer(() => node.loadNodeChildren())),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (_) => {
                    this.notificationService.success('Added successfully');
                    if (this.isSearchMode === false) {
                        this.getItemGroupings();
                    }
                    node.setIsExpanded(true);
                    node.setIsActive(true);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }


    toggleEnabled(node: TreeNode): void {
        const item = node.data.data;
        const disabled = ItemGroupingsComponent.isEnabled(node);
        this.dialogService
            .openConfirmDialog(`Are you sure you want to ${disabled ? 'enable' : 'disable'}`, item.name)
            .afterClosed()
            .pipe(
                filter(Boolean),
                tap((_) => {
                    item.disabled = !item.disabled;
                    node.treeModel.update();
                }),
                switchMap((_) => of(this.itemGroupingsFacade.toggleItemDisabled(item.tag, item.disabled))),
                tap((_) => {
                    const title = disabled ? 'Enabled' : 'Disabled';
                    this.notificationService.success(`${title}: ${item.name}`);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }}
            );
    }


    deleteItem(node: TreeNode): void {
        const item = node.data.data;
        const parent = node.parent?.data?.data;
        const dialogRef = this.dialog.open(DeleteItemDialogComponent, {
            width: '600px',
            height: '370px',
            data: {
                tag: item.name,
            },
        });
        dialogRef
            .afterClosed().pipe(
            filter((res) => !!res),
            // TODO: remove this ternary when the toggle is removed
            switchMap((res) => node.isRoot ? this.itemGroupingsFacade.api.deleteItem(item.tag) :
                this.itemGroupingsFacade.api.removeChildItem(parent?.tag, item.tag)),
            switchMap(() => node.isRoot ? of(null) : defer(() => node.parent.loadNodeChildren())),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
                next: (res) => {
                    this.notificationService.success(`Successfully deleted ${item.name}`);
                    if (this.isSearchMode === false) {
                        this.getItemGroupings();
                    } else {
                        this.nodes$.next(this.nodes$.value.filter(value => value.id !== item.tag));
                    }
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            }
        );
    }

    removeItem(node: TreeNode): void {
        const item = node.data.data;
        const parent = node.parent?.data?.data;
        const dialogRef = this.dialog.open(RemoveItemDialogComponent, {
            width: '600px',
            height: '370px',
            data: {
                tag: item.name,
                parentTag: parent?.tag,
            },
        });
        try {
            dialogRef
                .afterClosed().pipe(
                filter((res) => !!res),
                switchMap((res) => this.itemGroupingsFacade.api.removeChildItem(parent?.tag, item.tag)),
                switchMap(() => node.isRoot ? of(null) : defer(() => node.parent.loadNodeChildren())),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
                    next: (res) => {
                        this.notificationService.success(`Successfully removed ${item.name}`);
                        if (this.isSearchMode === false) {
                            this.getItemGroupings();
                        }
                    },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
                }
            );
        } catch (e) {
            this.notificationService.error(`Error removing ${item.name}`);
        }
    }

    selectItem(node: TreeNode): void {
        const selected: Item = node.data.data;
        if (RA.isNotNil(selected)) {
            this.checkIfItemHasChanges(this.prevItem)
                .pipe(take(1),takeUntilDestroyed(this.destroyRef))
                .subscribe((_) => {
                    this.prevItem = selected;
                    // CSS highlight
                    node.setIsActive(true);
                    // Show details in right panel
                    this.itemGroupingsFacade.selectItemGrouping(selected.tag);
                    this.itemsFacade.getItemLocale(selected.tag, 'US', '')
                        .pipe(
                            takeUntilDestroyed(this.destroyRef)
                        )
                        .subscribe(result =>
                        this.itemsFacade.itemLocaleDetail$.next(result)
                        )
                });
        } else {
            this.prevItem = null;
        }
    }


    checkIfItemHasChanges(prevItem: Item): Observable<boolean> {
        return this.isReadOnly$.pipe(
            take(1),
            switchMap(( isReadOnly) => {
                if (this.hasChanges() && !isReadOnly && RA.isNotNil(prevItem)) {
                    return this.dialogService
                        .openSaveChangesDialog(prevItem.name)
                        .afterClosed()
                        .pipe(
                            switchMap(res => {
                                if (res) {
                                    this.itemGroupingsForm.submit(prevItem.tag);
                                }
                                this.itemGroupingsForm.dynamicForms.forEach(df => {
                                    if (df.form.dirty) {
                                        this.itemGroupingsForm.cancel(df.attributeGroupId);
                                    }
                                })
                                return of(true);
                            })
                        );
                }
                return of(true);
            })
        );
    }


    hasChanges(): boolean {
        return  this.itemGroupingsForm?.hasChanges() ?? false;
    }

    getToggleValue(): boolean {
        let toggleValue;
        this.removeChildToggle$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value =>
            toggleValue = value);
        return toggleValue;
    }
}
